/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReveal } from '../hooks/useReveal';
import {
  FADEL_BANNER,
  FADEL_COPY,
  FADEL_DISHES,
  FADEL_LOGO,
  FADEL_SCREENS,
  FADEL_URL,
  FADEL_VERTICALS,
  fadelText,
} from '../../lib/fadel';
import { SHELL, SECTION, EYEBROW_DARK, H2, LEAD_DARK, BTN_PRIMARY, RULE } from '../theme';

/**
 * Annonce du lancement de FaDel Delivery, version site Tchad.
 *
 * Le pendant UK vit dans `components/FadelLaunch.jsx` : meme contenu, mais la
 * signature du Tchad est or sur bleu nuit la ou le UK est cyan sur ardoise. Un
 * composant unique pilote par une prop de theme melangeait les deux chartes
 * dans un fichier ; les visuels et les textes, eux, restent partages via
 * `lib/fadel.js`.
 *
 * Le catalogue de plats ne figure que de ce cote : ce sont des plats tchadiens
 * et camerounais, qui parlent au public de N'Djamena et n'apporteraient rien a
 * une page d'accueil britannique.
 */
export const FadelLaunch = () => {
  const { language } = useLanguage();
  const text = (entry) => fadelText(entry, language);
  const head = useReveal();
  const verticals = useReveal();
  const screens = useReveal();

  return (
    <section
      data-testid="tchad-home-fadel"
      className={`relative overflow-hidden bg-slate-950 text-white ${SECTION}`}
    >
      <div className="absolute inset-0 bg-tchad-weave" aria-hidden="true" />

      <div className={`relative ${SHELL}`}>
        <div ref={head.ref} className={`${head.className} grid gap-12 lg:grid-cols-12 lg:items-center`}>
          <div className="lg:col-span-5">
            <span className={EYEBROW_DARK}>{text(FADEL_COPY.eyebrow)}</span>
            <div className={`${RULE} mt-3`} />

            <img
              src={FADEL_LOGO}
              alt="FaDel Delivery"
              width="760"
              height="258"
              loading="lazy"
              className="mt-6 h-16 w-auto rounded-md sm:h-20"
            />

            <h2 className={`${H2} mt-6`}>{text(FADEL_COPY.title)}</h2>
            <p className={`mt-6 ${LEAD_DARK}`}>{text(FADEL_COPY.lead)}</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={FADEL_URL}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="tchad-fadel-cta"
                className={BTN_PRIMARY}
              >
                {text(FADEL_COPY.cta)}
                <ExternalLink className="h-4 w-4" />
              </a>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-3 py-1.5 text-xs font-semibold text-amber-300">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse-ring" />
                {text(FADEL_COPY.badge)}
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <img
              src={FADEL_BANNER}
              alt=""
              aria-hidden="true"
              width="1280"
              height="619"
              loading="lazy"
              className="w-full rounded-lg border border-white/10"
            />
          </div>
        </div>

        {/* Verticales : les pictogrammes de la fiche applicative disent en un
            coup d'oeil l'etendue de la plateforme, ce qu'un paragraphe met
            trois lignes a faire. */}
        <h3 className={`mt-20 ${EYEBROW_DARK}`}>{text(FADEL_COPY.verticalsTitle)}</h3>
        <ul
          ref={verticals.ref}
          className={`${verticals.className} mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8`}
        >
          {FADEL_VERTICALS.map(({ src, label }) => (
            <li
              key={src}
              data-testid="tchad-fadel-vertical"
              className="flex flex-col items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-4 text-center transition-colors hover:border-amber-400/40 hover:bg-white/[0.06]"
            >
              <img
                src={src}
                alt=""
                aria-hidden="true"
                width="320"
                height="320"
                loading="lazy"
                className="h-14 w-auto"
              />
              <span className="text-xs font-medium text-slate-300">{text(label)}</span>
            </li>
          ))}
        </ul>

        {/* Captures : defilement horizontal plutot qu'une grille. Neuf ecrans en
            grille prendraient toute la hauteur de la page d'accueil, alors
            qu'ils ne sont qu'un apercu. */}
        <h3 className={`mt-16 ${EYEBROW_DARK}`}>{text(FADEL_COPY.screensTitle)}</h3>
        <div
          ref={screens.ref}
          className={`${screens.className} mt-6 -mx-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8`}
        >
          <ul className="flex w-max gap-6">
            {FADEL_SCREENS.map(({ src, caption }) => (
              <li key={src} data-testid="tchad-fadel-screen" className="w-44 shrink-0 sm:w-52">
                {/* La legende est juste en dessous, en texte visible :
                    laisser le meme libelle en `alt` la ferait annoncer deux
                    fois de suite par un lecteur d'ecran. */}
                <img
                  src={src}
                  alt=""
                  aria-hidden="true"
                  width="550"
                  height="1100"
                  loading="lazy"
                  className="w-full drop-shadow-[0_16px_28px_rgba(0,0,0,0.55)]"
                />
                <p className="mt-4 text-start text-xs leading-relaxed text-slate-400">{text(caption)}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Catalogue : des plats de la region, photographies reelles, pour que
            l'annonce ne se resume pas a des maquettes d'interface. */}
        <h3 className={`mt-16 ${EYEBROW_DARK}`}>{text(FADEL_COPY.dishesTitle)}</h3>
        <ul className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {FADEL_DISHES.map(({ src, label }) => (
            <li
              key={src}
              data-testid="tchad-fadel-dish"
              className="group relative overflow-hidden rounded-lg border border-white/10"
            >
              <img
                src={src}
                alt={text(label)}
                loading="lazy"
                className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 to-transparent p-3 pt-8 text-xs font-medium text-white">
                {text(label)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
